import { useEffect, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import {
  DisplayFormattedDate,
  MoreOptions,
  Owners,
  TitleWithIcon
} from 'components/common';
import { GroupCourseTargetSchedule } from 'components/courses/common';
import {
  Box,
  BoxEmpty,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  ShareGroupsAdd,
  ShareGroupsRemove,
  ShareGroupsSendInvitation,
  ShareGroupsTargetSchedule,
  ShareGroupsUsers
} from '.';

import type { EmailTemplateData, Group, User } from 'shared-types';
import type { ApiDataHookUpdateHandler } from 'lib/api/useApiData';
import type { GetCourseGroupsRes } from 'shared-types/responses';

export type UpdateShareGroupsObject = {
  showLoading?: boolean;
};

export type ShareGroup = Group & {
  admins: User[];
  numberOfAttributeUsers: number;
  numberOfCourses: number;
  numberOfUsers: number;
  owners: User[];
  totalNumberOfUsers: number;
};

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: ApiDataHookUpdateHandler;
}

const ShareGroups = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const [availableGroups, setAvailableGroups] = useState<ShareGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<GetCourseGroupsRes['groups']>([]);

  useEffect(() => {
    updateGroups({});
  }, []);

  const updateGroups = async ({
    showLoading = true
  }: UpdateShareGroupsObject) => {
    try {
      if (showLoading) setLoading(true);

      const availableGroups = await api.get(
        '/admin/groups-available?includeGroupsWithAdminPermission=manage-courses'
      );
      setAvailableGroups(availableGroups.data.groups);

      const groups = await api.get<GetCourseGroupsRes>(
        `/admin/courses/${course.id}/groups`
      );
      setGroups(groups.data.groups);

      const courses = await api.get(`/admin/courses?id=${course.id}`);
      dispatch.updatePageState({ course: courses.data.courses[0] });
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  // Settings can only be saved for groups the admin could also add the course to
  const editableGroupIds = new Set(availableGroups.map((group) => group.id));

  return (
    <div className="groups">
      <Box
        headerContent={
          <>
            {availableGroups.length &&
            emailTemplates &&
            emailTemplatesDefault ? (
              <ShareGroupsAdd
                availableGroups={availableGroups}
                emailTemplates={emailTemplates}
                emailTemplatesDefault={emailTemplatesDefault}
                groups={groups}
                loading={loading}
                updateGroups={updateGroups}
              />
            ) : null}

            <ShareGroupsUsers groups={groups} updateShareUsers={updateUsers} />
          </>
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.commonGroups}
        {...(course?.userAccess === 'guests'
          ? {
              disabledOverlayContent: messages.coursesGuestsOnlyWarning,
              disabledOverlayIcon: 'exclamation-triangle'
            }
          : {})}
      >
        {groups.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell>{messages.commonGroup}</BoxTableCell>
              <BoxTableCell>{messages.groupsAddedUsers}</BoxTableCell>
              <BoxTableCell>{messages.userAttributes}</BoxTableCell>
              <BoxTableCell>{messages.analyticsTotalUsers}</BoxTableCell>
              <BoxTableCell>{messages.commonInviteEmail}</BoxTableCell>
              <BoxTableCell>{messages.commonMandatory}</BoxTableCell>
              <BoxTableCell>{messages.courseTargetSchedule}</BoxTableCell>
              <BoxTableCell></BoxTableCell>
            </BoxTableHeader>

            {groups.map((group) => (
              <BoxTableRow key={group.id}>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonGroup}
                >
                  <TitleWithIcon
                    icon="users"
                    title={group.title}
                    titleLink={{
                      as: `/groups/${group.id}`,
                      href: '/groups/[groupId]'
                    }}
                  >
                    <Owners owners={group.owners} />
                  </TitleWithIcon>
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.groupsAddedUsers}
                >
                  {group.numberOfUsers}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.userAttributes}
                >
                  {group.userAttributes.length}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.analyticsTotalUsers}
                >
                  {group.totalNumberOfUsers}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  emptyValue="-"
                  label={messages.commonInviteEmail}
                  {...(group.inviteEmailDate ? { icon: 'envelope' } : {})}
                >
                  {group.inviteEmailDate ? (
                    <DisplayFormattedDate date={group.inviteEmailDate} />
                  ) : null}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.commonMandatory}
                >
                  {course.isMandatory || group.isMandatory
                    ? messages.commonMandatory
                    : null}
                </BoxTableCell>

                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  label={messages.courseTargetSchedule}
                >
                  <GroupCourseTargetSchedule targetSchedule={group} />
                </BoxTableCell>

                <BoxTableCell
                  shrink={true}
                  verticalBreakpoint={breakpoints.medium}
                >
                  <MoreOptions styleContentButtons={false}>
                    {emailTemplates && emailTemplatesDefault ? (
                      <ShareGroupsSendInvitation
                        emailTemplates={emailTemplates}
                        emailTemplatesDefault={emailTemplatesDefault}
                        group={group}
                        updateGroups={() =>
                          updateGroups({ showLoading: false })
                        }
                      />
                    ) : null}

                    {editableGroupIds.has(group.id) ? (
                      <ShareGroupsTargetSchedule
                        course={course}
                        group={group}
                        isCourseMandatory={course.isMandatory}
                        refresh={() =>
                          Promise.all([
                            updateUsers(),
                            updateGroups({ showLoading: false })
                          ])
                        }
                      />
                    ) : null}

                    <ShareGroupsRemove
                      groupId={group.id}
                      updateGroups={updateGroups}
                    />
                  </MoreOptions>
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <BoxEmpty text={messages.groupsEmpty} />
        )}
      </Box>
    </div>
  );
};

export default ShareGroups;
